import { connect } from 'react-redux';
import { Form, FormRenderProps, useField } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { FEDERAL_LOW_248_GUID } from 'KnmPackage/MainModule/constants/erknmFederalLawUuid';

import { Modal } from '../../../UIPackage/ModalModule/components/Modal';
import styles from './CreateErknmModal.module.scss';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { Notification } from '../../../CorePackage/NotificationModule/components/Notification';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import {
  APPROVE_REQUIRED_BLOCK_ID,
  KIND_CONTROL_BLOCK_ID,
  KIND_KNM_BLOCK_ID,
  KNO_ORGANIZATION_BLOCK_ID,
  PROSECUTOR_ORGANIZATION_BLOCK_ID,
  TYPE_BLOCK_ID,
} from '../constants/knmErknmFieldsElementsIds';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { KnmTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { ErknmReasonItem } from '../../ErknmReasonsModule/components/ErknmReasonsItem';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { CheckNotificationModal } from '../../../CorePackage/MainModule/components/CheckNotificationModal';
import { CheckNotificationTypesEnum } from '../../../CorePackage/MainModule/enums/CheckNotificationTypesEnum';
import {
  getDictionariesKnmKindsSelectOptionsSelector,
  getDictionariesKnoOrganizationsSelectOptions,
  getDictionariesSupervisionTypesSelectOptionsSelector,
  getKnoSupervisionStartDateSelector,
} from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoOrganizationDictionary } from '../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { INotification } from '../../../CorePackage/NotificationModule/interfaces/INotification';
import { ApproveRequiredTypesEnum } from '../../../APIPackage/KnmModule/enums/ApproveRequiredTypesEnum';
import { IKnmTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IKnmErknmFormReason } from '../interfaces/IKnmErknmFormReason';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';
import { IKnmReasonTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { KNM_CREATE_ERROR } from '../../../CorePackage/MainModule/constants/notices';
import {
  getAccountOrganizationsWithDistrictData,
  getAccountProsecutorOrganizationsWithDistrictSelectOptions,
} from '../../../CorePackage/AccountModule/selectors/accountSelectors';
import { IUserOrganisationSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import {
  getKnmExcludeDates,
  getKnmIsSeveralOrganizations,
  getKnmPlannedActivities,
  getKnmProsecutorOrganizationId,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IKnmKind } from '../../../APIPackage/DictionariesModule/interfaces/IKnmKind';
import {
  getCatalogErknmKnmReasonTypesSelectOptionsSelector,
  getCatalogKnmTypes248SelectOptionsSelector,
  IKnmTypes248Params,
} from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { useCreateErknmModalDictionaries } from '../hooks/useCreateErknmModalDictionaries';
import { ITripletKnoSupervisionCondition } from '../../../CorePackage/DictionariesModule/interfaces/ITripletKnoSupervisionCondition';
import { ErknmInfoDates } from '../../ErknmInfoModule/components/ErknmInfoDates';
import { KnmInvalidFieldEnum } from '../../../KnmPackage/MainModule/enums/KnmInvalidFieldEnum';
import { isInvalidField } from '../common/validateInvalidFields';
import { knmInvalidFields } from '../../../KnmPackage/MainModule/constants/knmInvalidFields';
import { KnmFormValidator } from '../../../KnmPackage/MainModule/types/KnmFormValidator';
import { knmErknmValidator } from '../common/knmErknmValidator';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { IErknmInfoExcludeDatesResponse } from '../../ErknmInfoModule/interfaces/IErknmInfoExcludeDatesResponse';
import { isGeneralProsecutorOGRN } from '../../../CorePackage/MainModule/validators/isOgrn';

/*
  Новая реализация окна создания КНМ, отвязанная от формы редактирования КНМ.
  TODO После успешного тестирования данного окна, заменить реализацию CreateErknmModal на эту
 */

const emptyReason: IKnmErknmFormReason = {
  type: undefined,
  text: undefined,
  date: undefined,
  riskIndicators: undefined,
  requirementDetails: undefined,
  dateRequirement: undefined,
  assignmentNumber: undefined,
  assignmentDate: undefined,
  dateBegin: undefined,
  dateEnd: undefined,
  inspectionResults: undefined,
  listId: uuidv4(),
};

interface ICreateErknmModalProps {
  onClose: () => void;
  onSaveButtonClick: (values: IKnmForm) => void;
  visible: boolean;
  initialModalValues?: IKnmForm;
  invalidFields?: string[];
  isCopyEnabled?: boolean;
}

interface ICreateErknmModalStoreProps {
  knoSupervisionStartDateSelector: (
    knoOrganizationId: string | undefined,
    supervisionTypeId: string | undefined,
  ) => string | undefined;
  isSupervisor: boolean;
  settings: IAdminSetting[];
  excludeDates: IErknmInfoExcludeDatesResponse | undefined;
}

interface ICreateErknmModalRenderFormProps {
  onClose: () => void;
  visible: boolean;
  initialModalValues?: IKnmForm;
  invalidFields?: string[];
  isCopyEnabled?: boolean;
}

interface ICreateErknmModalRenderStoreProps {
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  isSeveralOrganizations: boolean | undefined;
  prosecutorOrganizationId: string | null | undefined;
  plannedActivities: boolean | undefined;
  userKnoOrganisations: IUserOrganisation[];
  supervisionTypesSelectOptionsSelector: (
    knoOrganizationId: string | undefined,
    isUseTransferringAuthorities: boolean,
    federalLawUUIDs: string[],
    isPlanned?: boolean,
  ) => IDictionarySupervisionTypeSelectOption[] | undefined;
  reasonTypesSelectOptionsSelector: (
    knoVersionId: string | undefined,
    supervisionVersionId: string | undefined,
    erknmTypeCode: string | undefined,
    knmCreated: string | null | undefined,
    knmKindId: string | undefined,
  ) => IKnmReasonTypeSelectOption[] | undefined;
  knmKindsSelectOptionsSelector: (
    triplet: ITripletKnoSupervisionCondition,
  ) => IDictionarySelectOption<IKnmKind>[] | undefined;
  catalogKnmTypes248SelectOptionsSelector: (params: IKnmTypes248Params) => IKnmTypeSelectOption[];
}

const CreateErknmModalRenderForm = ({
  knoOrganizationsSelectOptions,
  userProsecutorOrganisationsSelectOptions,
  isSeveralOrganizations,
  prosecutorOrganizationId,
  plannedActivities,
  userKnoOrganisations,
  supervisionTypesSelectOptionsSelector,
  reasonTypesSelectOptionsSelector,
  knmKindsSelectOptionsSelector,
  catalogKnmTypes248SelectOptionsSelector,
  form,
  values,
  onClose,
  visible,
  handleSubmit,
  initialModalValues,
  invalidFields,
  isCopyEnabled,
}: ICreateErknmModalRenderStoreProps & ICreateErknmModalRenderFormProps & FormRenderProps<IKnmForm>) => {
  const { startDate, stopDate, reasonsErknm, knoOrganizationErknm, kindControl, planGuid, typeErknm, kindKnm } = values;
  const knoOrganizationId = knoOrganizationErknm?.value.dictVersionId;
  const supervisionTypeId = kindControl?.value.dictVersionId;
  const reasonUuid =
    reasonsErknm?.map(x => x.reason?.ervkUuid)?.find(uuid => uuid) ||
    reasonsErknm?.map(x => x.type?.ervkUuid)?.find(uuid => uuid);

  const approveRequired = useField('approveRequired', { subscription: { value: true } }).input.value;
  const isApproveRequired = approveRequired.value === ApproveRequiredTypesEnum.REQUIRED;
  const typeErknmCode = typeErknm?.value;
  const kindKnmId = kindKnm?.value.dictVersionId;
  const isUnplanType = typeErknmCode === KnmTypeCodesEnum.VP;
  const isTypeErknmPP = typeErknmCode === KnmTypeCodesEnum.PP;

  const [isShowNotice, setIsShowNotice] = useState(false);
  const [errorNotice, setErrorNotice] = useState<INotification | undefined>(undefined);
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState<boolean>(false);

  const formRef = useRef(form);

  formRef.current = form;

  useEffect(() => {
    if (initialModalValues) {
      formRef.current.initialize(initialModalValues);
    } else {
      formRef.current.initialize({ reasonsErknm: [emptyReason] });
    }
  }, [initialModalValues, formRef]);

  const filteredUserProsecutorOrganisationsSelectOptions = isSeveralOrganizations
    ? userProsecutorOrganisationsSelectOptions.filter(item => item.id === prosecutorOrganizationId)
    : userProsecutorOrganisationsSelectOptions;

  const supervisionTypesSelectOptions = supervisionTypesSelectOptionsSelector(
    knoOrganizationId,
    isTypeErknmPP,
    [FEDERAL_LOW_248_GUID],
    false,
  );
  const reasonTypesSelectOptions = reasonTypesSelectOptionsSelector(
    knoOrganizationId,
    supervisionTypeId,
    typeErknmCode,
    undefined,
    kindKnmId,
  );
  const kindsSelectOptions =
    knoOrganizationId && supervisionTypeId
      ? knmKindsSelectOptionsSelector({
          knoVersionId: knoOrganizationId,
          supervisionVersionId: supervisionTypeId,
          conditionUuid: reasonUuid,
        })
      : [];
  const knmTypeSelectOption = catalogKnmTypes248SelectOptionsSelector({
    knoVersionId: knoOrganizationId,
    supervisionVersionId: supervisionTypeId,
  });

  const onSaveButtonClick = () => handleSubmit();

  const knmTypesTransformFunc = useCallback(
    (items: IKnmTypeSelectOption[]) => {
      if (!knoOrganizationErknm || !kindControl) return [];

      return plannedActivities || items;
    },
    [plannedActivities, knoOrganizationErknm, kindControl],
  );

  /**
   * useEffect для поиска и выбора предзаполненного значения для "Контрольный (надзорный) орган"
   */
  useEffect(() => {
    if (userKnoOrganisations && knoOrganizationsSelectOptions && !initialModalValues?.knoOrganizationErknm) {
      const byDefaultKno = userKnoOrganisations.find(i => i.byDefault);

      if (byDefaultKno) {
        const knoOrg = knoOrganizationsSelectOptions.find(i => i.idStr?.toString() === byDefaultKno.id);

        if (knoOrg) {
          form.change('knoOrganizationErknm', knoOrg);
        }
      }
    }
  }, [form, userKnoOrganisations, knoOrganizationsSelectOptions, initialModalValues]);

  const clearStartDateIsBeforeCurrentDate = useCallback(() => {
    if (startDate) {
      const currentDate = formatDate({ date: new Date(), dateFormat: SYSTEM_DATE_FORMAT_FNS });
      const formattedStartDate = formatDate({ date: startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS });

      if (formattedStartDate < currentDate) {
        form.change('startDate');
      }
    }
  }, [startDate, form]);

  const clearStopDateIsBeforeCurrentDate = useCallback(() => {
    if (stopDate) {
      const currentDate = formatDate({ date: new Date(), dateFormat: SYSTEM_DATE_FORMAT_FNS });
      const formattedStopDate = formatDate({ date: stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS });

      if (formattedStopDate < currentDate) {
        form.change('stopDate');
      }
    }
  }, [stopDate, form]);

  const handleKnoOrganizationChange = useCallback(() => {
    form.change('kindControl');
    form.change('kindKnm');
    form.change('reasonsErknm', [emptyReason]);
    form.change('approveRequired');
  }, [form]);

  const handleKindControlChange = useCallback(
    (value: ISelectOption) => {
      form.change('reasonsErknm', [emptyReason]);
      form.change('kindKnm');
      form.change('approveRequired');

      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
    },
    [form, clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const handleTypeSelectChange = useCallback(
    (value: ISelectOption) => {
      form.change('approved');
      form.change('planId');
      form.change('planGuid');
      form.change('kindKnm');
      form.change('reasonsErknm', [emptyReason]);
      form.change('reasonDocuments');
      form.change('approveRequired');

      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
    },
    [form, clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const handleApproveRequiredChange = useCallback(
    (value: ISelectOption) => {
      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
    },
    [clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const handleKindKnmChange = useCallback(
    (value: ISelectOption) => {
      if (!value) {
        clearStartDateIsBeforeCurrentDate();
        clearStopDateIsBeforeCurrentDate();
      }
      form.change('directDurationDays');
      form.change('durationHours');
    },
    [clearStartDateIsBeforeCurrentDate, clearStopDateIsBeforeCurrentDate],
  );

  const reasonsFieldName = 'reasonsErknm[0]';
  const reasonFieldValue: IKnmErknmFormReason | undefined = reasonsErknm ? reasonsErknm[0] : undefined;
  const typesSelectOptions: IKnmReasonTypeSelectOption[] | undefined = reasonTypesSelectOptions?.map(selectOption => ({
    ...selectOption,
    isDisabled: reasonsErknm?.some(
      reasonsFieldsValueItem =>
        reasonsFieldsValueItem.type?.id === selectOption.id && selectOption.id !== reasonFieldValue?.type?.id,
    ),
  }));

  const handleCloseNotification = useCallback(() => {
    setIsShowNotice(false);
  }, [setIsShowNotice]);

  /**
   * Обработчик нажатия на кнопку "Сохранить"
   */
  const onClickSaveButton = useCallback(() => {
    if (!onSaveButtonClick || !form) return;

    const isGeneralProsecutor = isGeneralProsecutorOGRN(form.getFieldState('prosecutorOrganizationErknm')?.value?.ogrn);

    const { hasValidationErrors: isValidationErrors } = form.getState();

    if (isValidationErrors) {
      form.submit();
      const error: INotification = {
        id: uuidv4(),
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.SAVE_KNM,
        renderContent: KNM_CREATE_ERROR,
      };

      setErrorNotice(error);
      setIsShowNotice(true);

      return;
    }

    setErrorNotice(undefined);
    setIsShowNotice(false);

    if (isUnplanType && isApproveRequired && isGeneralProsecutor) {
      setIsNotificationModalVisible(true);
    } else {
      onSaveButtonClick();
    }
  }, [form, onSaveButtonClick, isUnplanType, isApproveRequired]);

  /**
   * Функция подтверждения ознакомления с уведомлением
   */
  const onClickButtonApproveNotification = useCallback(() => {
    setIsNotificationModalVisible(false);
    if (onSaveButtonClick) {
      onSaveButtonClick();
    }
  }, [onSaveButtonClick]);

  const isValidFieldDisabled = useCallback(
    (field: KnmInvalidFieldEnum, guid?: string): boolean => {
      if (!Array.isArray(invalidFields)) {
        return false;
      }

      return knmInvalidFields[field].required || !isInvalidField(field, invalidFields, guid || '');
    },
    [invalidFields],
  );

  const isReasonFieldDisabled = isValidFieldDisabled(
    KnmInvalidFieldEnum.REASONS_ERKNM_ERVK_UUID,
    values.reasonsErknm?.[0]?.numGuid || undefined,
  );
  const isKindKnmDisabled = isValidFieldDisabled(KnmInvalidFieldEnum.KIND_KNM);

  useCreateErknmModalDictionaries(values);

  return (
    <Modal
      modalBodyClassName={styles.MainModal}
      modalClassName={styles.Modal}
      visible={visible}
      onClose={onClose}
      isOutsideClick={false}
    >
      <header className={styles.Header}>
        <Row margin="none">
          <div className={styles.HeaderTitle}>Создание контрольного (надзорного) мероприятия</div>
          {isShowNotice && errorNotice && (
            <div className={styles.NotificationWrapper}>
              <Notification notice={errorNotice} close={handleCloseNotification} />
            </div>
          )}
        </Row>
      </header>
      <ModalBody size="content" border="bottom" overflow="scroll">
        <Row notSpace notWrap>
          <div className={styles.InfoBody}>
            <div className={styles.TooltipIcon}></div>
            <div>Изменение отраженных ниже полей будет заблокировано после сохранения формы</div>
          </div>
        </Row>
        <Row>
          <FieldBlock
            label="Контрольный (надзорный) орган"
            placementLabel="left"
            id={KNO_ORGANIZATION_BLOCK_ID}
            className={styles.FieldWithOutTooltip}
          >
            <SelectInputField
              id="knoOrganizationErknm"
              name="knoOrganizationErknm"
              options={knoOrganizationsSelectOptions}
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={true}
              className={styles.FullWidth}
              validate={makeRequired('Поле не может быть пустым')}
              onChange={handleKnoOrganizationChange}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label="Орган прокуратуры"
            placementLabel="left"
            id={PROSECUTOR_ORGANIZATION_BLOCK_ID}
            className={styles.FieldWithOutTooltip}
          >
            <SelectInputField
              id="prosecutorOrganizationErknm"
              name="prosecutorOrganizationErknm"
              options={filteredUserProsecutorOrganisationsSelectOptions}
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={
                planGuid !== undefined || isValidFieldDisabled(KnmInvalidFieldEnum.PROSECUTOR_ORGANIZATION_ERKNM)
              }
              className={styles.FullWidth}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
        <Row notWrap>
          <FieldBlock label="Вид контроля (надзора)" placementLabel="left" id={KIND_CONTROL_BLOCK_ID}>
            <SelectInputField
              id="kindControl"
              name="kindControl"
              options={supervisionTypesSelectOptions}
              placeholder="Выберите значение"
              isSearchable
              disabled={
                (planGuid !== undefined && !!kindControl) || isValidFieldDisabled(KnmInvalidFieldEnum.KIND_CONTROL)
              }
              className={styles.FullWidth}
              validate={makeRequired('Поле не может быть пустым')}
              onChange={handleKindControlChange}
            />
          </FieldBlock>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующем поле:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </Row>
        <Row notWrap>
          <FieldBlock label="Характер мероприятия" placementLabel="left" id={TYPE_BLOCK_ID}>
            <SelectInputField
              id="typeErknm"
              name="typeErknm"
              options={knmTypeSelectOption?.filter(option => option.code !== KnmTypeCodesEnum.PP248P294)}
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={planGuid !== undefined || isValidFieldDisabled(KnmInvalidFieldEnum.TYPE_ERKNM)}
              className={styles.FullWidth}
              transformFunc={knmTypesTransformFunc}
              onChange={handleTypeSelectChange}
            />
          </FieldBlock>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующем поле:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                  <p>Вид контроля (надзора)</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </Row>
        <ErknmInfoDates
          isApproveRequired={isApproveRequired}
          typeCode={typeErknm?.value}
          formRef={formRef}
          isNewKnm={true}
          isWatchOnly={false}
          isCreateModal={true}
          isCopyEnabled={isCopyEnabled}
        />

        <FieldBlock label="Основание проведения мероприятия">
          <Row notWrap>
            <ErknmReasonItem
              watchOnly={true}
              reasonFieldValue={reasonFieldValue}
              reasonsFieldName={reasonsFieldName}
              sIndex={0}
              typesSelectOptions={typesSelectOptions}
              isNewKnm={true}
              formRef={formRef}
              created={null}
              isDisabled={isReasonFieldDisabled}
              isCreateModal={true}
            />
            <Tooltip
              placement="right"
              type="info"
              content={
                <div className="shared-tooltip-info-content">
                  Поле зависит от значения в следующем поле:
                  <ul>
                    <p>Контрольный (надзорный) орган</p>
                    <p>Вид контроля (надзора)</p>
                    <p>Характер мероприятия</p>
                  </ul>
                </div>
              }
            >
              <span className="shared-tooltip-info-icon" />
            </Tooltip>
          </Row>
        </FieldBlock>
        <Row margin="none">
          <FieldBlock
            label="Необходимость согласования"
            size="half"
            id={APPROVE_REQUIRED_BLOCK_ID}
            className={styles.FieldWithOutTooltip}
          >
            <SelectInputField
              id="approveRequired"
              name="approveRequired"
              placeholder="Выберите значение"
              disabled={true}
              size="medium"
              validate={makeRequired('Поле не может быть пустым')}
              defaultValue={undefined}
              onChange={handleApproveRequiredChange}
            />
          </FieldBlock>
          <FieldBlock size="half" label="Вид мероприятия">
            <Row notWrap>
              <FieldBlock id={KIND_KNM_BLOCK_ID}>
                <SelectInputField
                  id="kindKnm"
                  name="kindKnm"
                  options={kindsSelectOptions}
                  placeholder="Выберите значение"
                  size="medium"
                  onChange={handleKindKnmChange}
                  disabled={isReasonFieldDisabled && isKindKnmDisabled && !!kindKnm}
                />
              </FieldBlock>
              <Tooltip
                placement="right"
                type="info"
                content={
                  <div className="shared-tooltip-info-content">
                    Поле зависит от значения в следующем поле:
                    <ul>
                      <p>Контрольный (надзорный) орган</p>
                      <p>Вид контроля (надзора)</p>
                      <p>Характер мероприятия</p>
                      <p>Основание проведения мероприятия</p>
                    </ul>
                  </div>
                }
              >
                <span className="shared-tooltip-info-icon" />
              </Tooltip>
            </Row>
          </FieldBlock>
        </Row>
      </ModalBody>
      <ModalActions>
        <Button text="Сохранить" id="saveButton" size="medium" onClick={onClickSaveButton} color="primary" />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
      <CheckNotificationModal
        notificationType={CheckNotificationTypesEnum.FOR_CREATE_KNM_WITH_GENERAL_PROSECUTOR}
        visible={isNotificationModalVisible}
        onConfirm={onClickButtonApproveNotification}
      />
    </Modal>
  );
};

const CreateErknmModalWithoutStore = ({
  visible,
  onSaveButtonClick,
  knoSupervisionStartDateSelector,
  isSupervisor,
  settings,
  excludeDates,
  initialModalValues,
  isCopyEnabled,
  ...rest
}: ICreateErknmModalProps & ICreateErknmModalStoreProps & ICreateErknmModalRenderStoreProps) => {
  const handleSubmit = (values: IKnmForm) => {
    onSaveButtonClick(values);
  };

  const renderForm = (props: FormRenderProps<IKnmForm>) => {
    return (
      <CreateErknmModalRenderForm {...props} {...rest} visible={visible} initialModalValues={initialModalValues} />
    );
  };

  const validateForm = (values: IKnmForm) => {
    const knoOrganizationId = values.knoOrganizationErknm?.value.dictVersionId;
    const supervisionTypeId = values.kindControl?.value.dictVersionId;

    const validatorProps: KnmFormValidator = {
      knoSupervisionStartDate: knoSupervisionStartDateSelector(knoOrganizationId, supervisionTypeId),
      isNewKnm: true,
      isCopyEnabled,
      isSupervisor,
      settings,
      excludeDates,
      initialErknmValues: initialModalValues,
    };

    return knmErknmValidator({ values, ...validatorProps });
  };

  if (visible) {
    return (
      <Form<IKnmForm>
        render={renderForm}
        onSubmit={handleSubmit}
        mutators={{ ...arrayMutators }}
        validate={validateForm}
      />
    );
  }

  return null;
};

const mapStateToProps = (state: IRootState): ICreateErknmModalStoreProps & ICreateErknmModalRenderStoreProps => ({
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsWithDistrictSelectOptions(state),
  isSeveralOrganizations: getKnmIsSeveralOrganizations(state),
  prosecutorOrganizationId: getKnmProsecutorOrganizationId(state),
  plannedActivities: getKnmPlannedActivities(state),
  userKnoOrganisations: getAccountOrganizationsWithDistrictData(state).kno,
  supervisionTypesSelectOptionsSelector: getDictionariesSupervisionTypesSelectOptionsSelector(state),
  reasonTypesSelectOptionsSelector: getCatalogErknmKnmReasonTypesSelectOptionsSelector(state),
  knmKindsSelectOptionsSelector: getDictionariesKnmKindsSelectOptionsSelector(state),
  catalogKnmTypes248SelectOptionsSelector: getCatalogKnmTypes248SelectOptionsSelector(state),
  knoSupervisionStartDateSelector: getKnoSupervisionStartDateSelector(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  settings: getAdminFetchSettings(state),
  excludeDates: getKnmExcludeDates(state),
});

export const CreateErknmModalNew = connect(mapStateToProps)(CreateErknmModalWithoutStore);
